import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ownerStamp } from '../recordOwner';
import HeaderActionsPortal from './HeaderActionsPortal';
import ModuleEditPermissions from './ModuleEditPermissions';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';
import { Plus, Edit2, Trash2, CheckCircle, XCircle, Search, Layers, Clock, AlertCircle, RefreshCw, Download, Upload, MapPin, X, Shield } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import Papa from 'papaparse';
import { formatDateTime } from '../utils/dateFormatter';

const CampusManagement = () => {
  const { t } = useT();
  // Booking statuses share their keys with the booker's screen, so both sides of one record
  // read the same word.
  const bookingStatusWord = (v) => t(
    `facilityBooking.statuses.${String(v || '').toLowerCase().replace(/[^a-z]/g, '')}`, {}, v,
  );
  const [activeTab, setActiveTab] = useState('facilities');
  const [facilities, setFacilities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // New Facility Form State
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingFacilityId, setEditingFacilityId] = useState(null);
  const [newFacility, setNewFacility] = useState({ facilityId: '', lid: '', name: '', nameEn: '', nameZh: '', status: 'Available', autoApprove: false, capacity: '', bldg: '', floor: '' });
  const [systemLocations, setSystemLocations] = useState([]);
  const [systemFloors, setSystemFloors] = useState([]);

  const { activeSchoolId, isMobile, closeSidebar } = useOutletContext();

  const closeFacilityForm = () => {
    setShowAddForm(false);
    setEditingFacilityId(null);
  };

  // Shared dialog behaviour: retract the mobile sidebar (a fixed overlay that would otherwise sit on
  // top of the sheet), stop the page behind from scrolling, and close on Escape.
  useEffect(() => {
    if (!showAddForm) return;
    if (isMobile) closeSidebar?.();

    const onKey = (e) => { if (e.key === 'Escape') closeFacilityForm(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [showAddForm, isMobile, closeSidebar]);

  useEffect(() => {
    if (activeSchoolId) fetchData();
  }, [activeSchoolId]);

  const fetchData = async () => {
    if (!activeSchoolId) return;
    setLoading(true);
    try {
      // System Locations (Parameters)
      let locSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'));
      if (!locSnap.exists() || !locSnap.data().dropdowns?.locations) {
        locSnap = await getDoc(doc(db, 'metadata', 'system_settings'));
      }
      let locList = [];
      if (locSnap.exists() && locSnap.data().dropdowns?.locations) {
        locList = locSnap.data().dropdowns.locations
          .filter(l => l.active !== false && (l.status === 'Open' || l.status === 'ACT' || !l.status))
          .map(l => ({ id: l.code, name: l.nameEn || l.nameZh || l.name, nameEn: l.nameEn || '', nameZh: l.nameZh || '', capacity: l.capacity, seq: l.seq || 0, bldg: l.bldg || '', floor: l.floor || '' }))
          .sort((a, b) => a.seq - b.seq);
      }
      setSystemLocations(locList);

      let floorList = [];
      if (locSnap.exists() && locSnap.data().dropdowns?.floors) {
        floorList = locSnap.data().dropdowns.floors;
      }
      setSystemFloors(floorList);

      // Bookings
      const bSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'location_bookings')));
      const bList = [];
      bSnap.forEach(d => bList.push({ id: d.id, ...d.data() }));
      bList.sort((a, b) => (b.requestDate?.seconds || 0) - (a.requestDate?.seconds || 0));
      setBookings(bList);

      // Facilities
      const fSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'campus_facilities')));
      const fList = [];
      fSnap.forEach(d => fList.push({ id: d.id, ...d.data() }));
      fList.sort((a, b) => (a.lid || '').localeCompare(b.lid || ''));
      setFacilities(fList);

    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleEditFacility = (facility) => {
    setEditingFacilityId(facility.id);
    setNewFacility({
      facilityId: facility.facilityId || '',
      lid: facility.lid || '',
      name: facility.name || '',
      nameEn: facility.nameEn || '',
      nameZh: facility.nameZh || '',
      status: facility.status || 'Available',
      autoApprove: facility.autoApprove || false,
      capacity: facility.capacity || '',
      bldg: facility.bldg || '',
      floor: facility.floor || ''
    });
    setShowAddForm(true);
  };

  const handleAddFacility = async (e) => {
    e.preventDefault();
    if (!newFacility.lid || !activeSchoolId) return;

    try {
      const facilityToSave = { ...newFacility, schoolId: activeSchoolId };
      if (!facilityToSave.facilityId) {
        facilityToSave.facilityId = `FAC-${Date.now().toString().slice(-6)}`;
      }

      if (editingFacilityId) {
        await updateDoc(doc(db, 'schools', activeSchoolId, 'campus_facilities', editingFacilityId), facilityToSave);
      } else {
        await addDoc(collection(db, 'schools', activeSchoolId, 'campus_facilities'), { ...facilityToSave, ...ownerStamp() });
      }
      setNewFacility({ facilityId: '', lid: '', name: '', nameEn: '', nameZh: '', status: 'Available', autoApprove: false, capacity: '', bldg: '', floor: '' });
      setShowAddForm(false);
      setEditingFacilityId(null);
      fetchData();
    } catch (e) {
      alert(t('campusManagement.addFailed', { message: e.message }));
    }
  };

  const handleExportCSV = () => {
    const csvData = facilities.map(f => ({
      'Facility ID': f.facilityId || '',
      'Location Code': f.lid || '',
      'Building': f.bldg || '',
      'Floor': f.floor || '',
      'Facility Name': f.name || '',
      'Capacity': f.capacity || '',
      'Status': f.status || 'Available',
      'Auto Approve': f.autoApprove ? 'TRUE' : 'FALSE'
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Campus_Facilities_Export.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const parsedData = results.data;
        let successCount = 0;
        let errorCount = 0;

        for (const row of parsedData) {
          const lid = row['Location Code']?.trim();
          if (!lid) continue;

          // Attempt to map from systemLocations
          const sysLoc = systemLocations.find(l => l.id === lid);
          const defaultName = sysLoc ? sysLoc.name : (row['Facility Name']?.trim() || lid);
          const defaultNameEn = sysLoc ? sysLoc.nameEn : '';
          const defaultNameZh = sysLoc ? sysLoc.nameZh : '';
          const defaultCap = sysLoc ? sysLoc.capacity : (row['Capacity']?.trim() || '');
          const defaultBldg = sysLoc ? sysLoc.bldg : '';
          const defaultFloor = sysLoc ? sysLoc.floor : '';
          
          let fid = row['Facility ID']?.trim();
          if (!fid) {
            fid = `FAC-${Date.now().toString().slice(-6)}-${Math.floor(Math.random()*1000)}`;
          }

          const facilityData = {
            schoolId: activeSchoolId,
            facilityId: fid,
            lid: lid,
            name: row['Facility Name']?.trim() || defaultName,
            nameEn: defaultNameEn,
            nameZh: defaultNameZh,
            capacity: row['Capacity']?.trim() || defaultCap,
            bldg: defaultBldg,
            floor: defaultFloor,
            status: row['Status']?.trim() || 'Available',
            autoApprove: row['Auto Approve']?.trim().toUpperCase() === 'TRUE'
          };

          try {
            const existingFacility = facilities.find(f => f.lid === lid);
            if (existingFacility) {
              await updateDoc(doc(db, 'schools', activeSchoolId, 'campus_facilities', existingFacility.id), facilityData);
            } else {
              await addDoc(collection(db, 'schools', activeSchoolId, 'campus_facilities'), { ...facilityData, ...ownerStamp() });
            }
            successCount++;
          } catch (err) {
            console.error("Error importing row:", row, err);
            errorCount++;
          }
        }
        
        alert(t('campusManagement.imported', { ok: successCount, failed: errorCount }));
        fetchData();
        e.target.value = null;
      },
      error: (err) => {
        alert(t('campusManagement.parseFailed', { message: err.message }));
      }
    });
  };

  const handleDeleteFacility = async (id) => {
    if (!window.confirm(t('campusManagement.deleteConfirm'))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'campus_facilities', id));
      fetchData();
    } catch (e) {
      alert(t('campusManagement.deleteFailed', { message: e.message }));
    }
  };

  const handleBookingAction = async (bookingId, newStatus) => {
    try {
      await updateDoc(doc(db, 'schools', activeSchoolId, 'location_bookings', bookingId), { status: newStatus });
      fetchData();
    } catch (e) {
      alert(t('campusManagement.error', { message: e.message }));
    }
  };

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%' }}>
      
      {/* Tabs */}
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {[
            { id: 'facilities', label: t('campusManagement.tabs.facilities'), icon: Layers },
            { id: 'bookings', label: t('campusManagement.tabs.approvals'), icon: AlertCircle },
            { id: 'settings', label: t('campusManagement.tabs.settings'), icon: Shield }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
              >
                <div className="theme-tab-icon">
                  <Icon size={20} />
                </div>
                {tab.label}
              </button>
            )
          })}
        </div>
        
        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '1rem', padding: '0.5rem 0' }}>
          {activeTab === 'facilities' && (
            <>
              <button onClick={handleExportCSV} title={t('campusManagement.exportCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 1rem', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--color-text-main)' }}>
                <Download size={16} />
              </button>
              <label title={t('campusManagement.importCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.5rem 1rem', fontSize: '0.875rem', backgroundColor: 'var(--color-bg-app)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', color: 'var(--color-text-main)', margin: 0 }}>
                <Upload size={16} />
                <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportCSV} />
              </label>
              <button className="btn-primary" onClick={() => { setShowAddForm(true); setEditingFacilityId(null); setNewFacility({ facilityId: '', lid: '', name: '', nameEn: '', nameZh: '', status: 'Available', autoApprove: false, capacity: '', bldg: '', floor: '' }); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Plus size={16} /> Add Facility
              </button>
            </>
          )}
          <button onClick={fetchData} className="btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
        </HeaderActionsPortal>
      </div>

      {/* Add/Edit Facility Dialog -- a modal rather than an inline panel so opening it never reflows
          or pushes down the facility table behind it. */}
      {showAddForm && (
        <div
          onClick={closeFacilityForm}
          style={{
            position: 'fixed', inset: 0, zIndex: 120,
            backgroundColor: 'var(--color-overlay)',
            display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
            padding: isMobile ? 0 : '2rem',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--color-bg-card)',
              borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              boxShadow: 'var(--shadow-lg)',
              width: '100%',
              maxWidth: isMobile ? '100%' : '820px',
              maxHeight: isMobile ? '92vh' : '90vh',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>{editingFacilityId ? t('campusManagement.editFacility') : t('campusManagement.addFacility')}</h3>
              <button type="button" onClick={closeFacilityForm} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>
          <form onSubmit={handleAddFacility} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('campusManagement.facilityId')}</label>
              <input type="text" className="input-field" placeholder={t('campusManagement.facilityIdPlaceholder')} value={newFacility.facilityId} onChange={e => setNewFacility({...newFacility, facilityId: e.target.value})} />
            </div>
            <div style={{ flex: 1, minWidth: '300px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('campusManagement.locationCode')}</label>
              <select className="input-field" value={newFacility.lid} onChange={e => {
                const selectedLoc = systemLocations.find(l => l.id === e.target.value);
                setNewFacility({...newFacility, lid: e.target.value, name: selectedLoc ? selectedLoc.name : '', nameEn: selectedLoc ? selectedLoc.nameEn : '', nameZh: selectedLoc ? selectedLoc.nameZh : '', capacity: selectedLoc ? selectedLoc.capacity : '', bldg: selectedLoc ? selectedLoc.bldg : '', floor: selectedLoc ? selectedLoc.floor : ''});
              }} required>
                <option value="" disabled>{t('campusManagement.selectLocation')}</option>
                {systemLocations.map(loc => <option key={loc.id} value={loc.id}>{loc.id} - {loc.name} {loc.capacity ? t('campusManagement.capShort', { capacity: loc.capacity }) : ''}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('campusManagement.status')}</label>
              <select className="input-field" value={newFacility.status} onChange={e => setNewFacility({...newFacility, status: e.target.value})}>
                <option value="Available">{t('campusManagement.available')}</option>
                <option value="Unavailable">{t('campusManagement.unavailable')}</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('campusManagement.approvalType')}</label>
              <select className="input-field" value={newFacility.autoApprove} onChange={e => setNewFacility({...newFacility, autoApprove: e.target.value === 'true'})}>
                <option value="false">{t('campusManagement.requiresAdminApproval')}</option>
                <option value="true">{t('campusManagement.autoApproveRequests')}</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
              <button type="button" className="btn-outline" onClick={closeFacilityForm}>{t('common.cancel')}</button>
              <button type="submit" className="btn-primary">{t('campusManagement.saveFacility')}</button>
            </div>
          </form>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, color: 'var(--color-text-muted)' }}>
            <RefreshCw className="animate-spin" size={24} style={{ marginRight: '0.5rem' }} /> Loading Data...
          </div>
        ) : activeTab === 'facilities' ? (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)' }}>
                <tr>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.facilityId')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.location')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.building')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.floor')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.facilityName')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.capacity')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.status')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.rules')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', textAlign: 'right' }}>{t('campusManagement.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {facilities.length === 0 ? <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('campusManagement.noFacilities')}</td></tr> :
                facilities.map(f => (
                  <tr key={f.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '1rem', fontFamily: 'monospace', fontSize: '0.875rem' }}>{f.facilityId}</td>
                    <td style={{ padding: '1rem', fontFamily: 'monospace', fontSize: '0.875rem' }}>{f.lid}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>{f.bldg || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      {(() => {
                         const floorCode = f.floor;
                         const floorObj = systemFloors.find(fl => fl.code === floorCode);
                         return floorObj ? (floorObj.nameEn || floorObj.name) : (floorCode || '-');
                      })()}
                    </td>
                    <td style={{ padding: '1rem', fontWeight: '600' }}>
                      {f.name}
                    </td>
                    <td style={{ padding: '1rem', color: 'var(--color-text-muted)' }}>{f.capacity || '-'}</td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600',
                        backgroundColor: f.status === 'Available' ? 'var(--color-success-bg)' : 'var(--color-danger-bg)',
                        color: f.status === 'Available' ? 'var(--color-success-text)' : 'var(--color-danger-text)'
                      }}>
                        {f.status}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                      {f.autoApprove ? t('campusManagement.autoApprove') : t('campusManagement.requiresReview')}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button onClick={() => handleEditFacility(f)} className="btn-outline" style={{ padding: '0.4rem', color: 'var(--color-primary)', width: 'auto', display: 'inline-flex' }}>
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDeleteFacility(f.id)} className="btn-outline" style={{ padding: '0.4rem', color: 'var(--color-danger)', width: 'auto', display: 'inline-flex' }}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'settings' ? (
          <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 0 : '1.5rem' }}>
            <ModuleEditPermissions
              settingsDoc="campus_settings"
              moduleId="campus_management"
              recordLabel="campus facilities"
              approveLabel="grant or refuse facility bookings"
            />
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)' }}>
                <tr>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.bookingId')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.location')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.user')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.dateTime')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('campusManagement.status')}</th>
                  <th style={{ padding: '1rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', textAlign: 'right' }}>{t('campusManagement.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 ? <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('campusManagement.noBookings')}</td></tr> :
                bookings.map(b => (
                  <tr key={b.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '1rem', fontFamily: 'monospace', fontSize: '0.875rem' }}>{b.bookingId}</td>
                    <td style={{ padding: '1rem', fontWeight: '600' }}>{b.locationName}</td>
                    <td style={{ padding: '1rem' }}>{b.userId}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                      <div>{b.startDate?.toDate ? formatDateTime(b.startDate.toDate()) : b.startDate ? formatDateTime(new Date(b.startDate)) : b.bookingDate}</div>
                      <div>to {b.endDate?.toDate ? formatDateTime(b.endDate.toDate()) : b.endDate ? formatDateTime(new Date(b.endDate)) : b.timeSlot}</div>
                      {b.remarks && <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--color-text-main)', fontStyle: 'italic' }}>{t('campusManagement.remarks', { remarks: b.remarks })}</div>}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{ 
                        padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600',
                        backgroundColor: b.status === 'Approved' ? 'var(--color-success-bg)' : b.status === 'Pending' ? 'var(--color-warning-bg)' : 'var(--color-danger-bg)',
                        color: b.status === 'Approved' ? 'var(--color-success-text)' : b.status === 'Pending' ? 'var(--color-warning-text)' : 'var(--color-danger-text)'
                      }}>
                        {bookingStatusWord(b.status)}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'right', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      {b.status === 'Pending' && (
                        <>
                          <button onClick={() => handleBookingAction(b.id, 'Approved')} className="btn-primary" style={{ padding: '0.4rem', fontSize: '0.75rem', backgroundColor: 'var(--color-success)' }}>
                            {t('campusManagement.approve')}
                          </button>
                          <button onClick={() => handleBookingAction(b.id, 'Rejected')} className="btn-primary" style={{ padding: '0.4rem', fontSize: '0.75rem', backgroundColor: 'var(--color-danger)' }}>
                            {t('campusManagement.reject')}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
};

export default CampusManagement;
